import React, { createContext, useContext, useState } from 'react';
import axios from 'axios';

const IntakeContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const IntakeProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [patientInfo, setPatientInfo] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [intakeData, setIntakeData] = useState({
    consent: false,
    demographics: null,
    stopBang: null,
    epworth: null,
    isi: null
  });

  const validateToken = async (intakeToken) => {
    try {
      const response = await axios.get(`${API_URL}/api/intake/validate/${intakeToken}`);
      setToken(intakeToken);
      setPatientInfo(response.data.patient);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Invalid or expired intake link'
      };
    }
  };

  const submitConsent = async () => {
    try {
      await axios.post(`${API_URL}/api/intake/consent/${token}`, {
        consent_given: true
      });
      setIntakeData({ ...intakeData, consent: true });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to submit consent'
      };
    }
  };

  const submitDemographics = async (demographics) => {
    try {
      await axios.post(`${API_URL}/api/intake/demographics/${token}`, demographics);
      setIntakeData({ ...intakeData, demographics });
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to submit demographics'
      };
    }
  };

  const submitStopBang = async (responses) => {
    try {
      const response = await axios.post(`${API_URL}/api/intake/stop-bang/${token}`, {
        responses
      });
      setIntakeData({ ...intakeData, stopBang: { responses, score: response.data.score } });
      return { success: true, score: response.data.score };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to submit STOP-BANG'
      };
    }
  };

  const submitEpworth = async (responses) => {
    try {
      const response = await axios.post(`${API_URL}/api/intake/epworth/${token}`, {
        responses
      });
      setIntakeData({ ...intakeData, epworth: { responses, score: response.data.score } });
      return { success: true, score: response.data.score };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to submit Epworth'
      };
    }
  };

  const submitISI = async (responses) => {
    try {
      const response = await axios.post(`${API_URL}/api/intake/isi/${token}`, {
        responses
      });
      setIntakeData({ ...intakeData, isi: { responses, score: response.data.score } });
      return { success: true, score: response.data.score };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to submit ISI'
      };
    }
  };

  const completeIntake = async (intakeToken = null) => {
    const useToken = intakeToken || token;
    try {
      const response = await axios.post(`${API_URL}/api/intake/complete/${useToken}`);
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to complete intake'
      };
    }
  };

  return (
    <IntakeContext.Provider
      value={{
        token,
        patientInfo,
        currentStep,
        setCurrentStep,
        intakeData,
        validateToken,
        submitConsent,
        submitDemographics,
        submitStopBang,
        submitEpworth,
        submitISI,
        completeIntake
      }}
    >
      {children}
    </IntakeContext.Provider>
  );
};

export const useIntake = () => {
  const context = useContext(IntakeContext);
  if (!context) {
    throw new Error('useIntake must be used within IntakeProvider');
  }
  return context;
};
